<template>
  <div class="login-container dark-theme">
    <div class="lang-switch">
      <a-dropdown>
        <button class="lang-btn">
          <a-icon type="global" class="l-icon" /> {{ $i18n.locale === 'zh-CN' ? '中' : 'EN' }}
        </button>
        <a-menu slot="overlay" @click="handleLangClick" class="custom-dark-menu">
          <a-menu-item key="zh-CN">🇨🇳 简体中文</a-menu-item>
          <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>

    <!-- 动态背景对齐首页风格 -->
    <div class="hero-bg">
      <div class="glow-blob center-blob"></div>
      <div class="grid-overlay"></div>
      
      <!-- 悬浮点缀元素 -->
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
      <div class="floating-shape shape-4">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle></svg>
      </div>
      <div class="floating-shape shape-5">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 22h20L12 2z"></path></svg>
      </div>
    </div>

    <div class="login-content relative-z">
      <div class="brand-section">
        <div class="logo-wrapper" @click="$router.push('/')">
          <img src="/logo.png" alt="logo" class="brand-logo-img" />
          <h1 class="brand-text">spaceproxy</h1>
        </div>
        <p class="brand-slogan">{{ $t('login.slogan') }}</p>
      </div>

      <div class="login-card bento-card">
        <div class="card-header">
          <h2>{{ $t('login.welcomeBack') }}</h2>
          <p>{{ $t('login.signInToContinue') }}</p>
        </div>

        <a-form-model :model="form" :rules="rules" ref="loginForm" class="login-form">
          <a-form-model-item prop="username">
            <div class="input-wrapper">
              <a-icon type="user" class="input-icon" />
              <a-input 
                v-model="form.username" 
                :placeholder="$t('login.usernamePlaceholder')" 
                size="large" 
                :bordered="false"
              />
            </div>
          </a-form-model-item>
          
          <a-form-model-item prop="password">
            <div class="input-wrapper">
              <a-icon type="lock" class="input-icon" />
              <a-input-password 
                v-model="form.password" 
                :placeholder="$t('login.passwordPlaceholder')" 
                size="large" 
                :bordered="false"
              />
            </div>
          </a-form-model-item>

          <a-form-model-item prop="verifyCode">
            <div class="captcha-row">
              <div class="input-wrapper captcha-input">
                <a-icon type="safety-certificate" class="input-icon" />
                <a-input 
                  v-model="form.verifyCode" 
                  :placeholder="$t('login.captchaPlaceholder')" 
                  size="large" 
                  :bordered="false"
                />
              </div>
              <div class="captcha-img-wrapper" @click="getCaptcha" title="点击刷新">
                <img :src="captchaUrl" alt="验证码" v-if="captchaUrl" />
                <div v-else class="captcha-placeholder">
                  <a-icon type="reload" />
                </div>
              </div>
            </div>
          </a-form-model-item>
          
          <div class="form-options">
            <a-checkbox v-model="form.remember">{{ $t('login.rememberMe') }}</a-checkbox>
            <!-- <a class="forgot-link">{{ $t('login.forgotPassword') }}</a> -->
          </div>
          
          <button 
            class="btn-primary huge full-width submit-btn"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            @click="handleSubmit"
            type="button"
          >
            <a-icon type="loading" v-if="loading" />
            <span v-else>{{ $t('login.loginBtn') }}</span>
          </button>
          
          <div class="register-link">
            {{ $t('login.noAccount') }} <router-link to="/register">{{ $t('login.registerNow') }}</router-link>
          </div>
        </a-form-model>
      </div>
    </div>
    
    <div class="footer-copyright relative-z">
      Copyright © {{ new Date().getFullYear() }} spaceproxy. All Rights Reserved.
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';

export default {
  name: 'UserLogin',
  data() {
    return {
      captchaUrl: '',
      loading: false,
      form: {
        username: '',
        password: '',
        verifyCode: '',
        remember: true
      }
    };
  },
  computed: {
    rules() {
      return {
        username: [{ required: true, message: this.$t('login.inputUsername'), trigger: 'blur' }],
        password: [{ required: true, message: this.$t('login.inputPassword'), trigger: 'blur' }],
        verifyCode: [{ required: true, message: this.$t('login.inputCaptcha'), trigger: 'blur' }]
      };
    }
  },
  mounted() {
    this.getCaptcha();
  },
  methods: {
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    getCaptcha() {
      request.get('/auth/captcha', { responseType: 'arraybuffer' })
        .then(res => {
          const data = res.data || res;
          if (data) {
            const base64 = btoa(new Uint8Array(data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
            this.captchaUrl = `data:image/png;base64,${base64}`;
          }
        })
        .catch(err => {
          console.error('获取验证码失败:', err);
          this.$message.error('获取验证码失败');
        });
    },
    handleSubmit() {
      this.$refs.loginForm.validate(async valid => {
        if (!valid) return;

        this.loading = true;
        try {
          const res = await request.post('/auth/login', {
            username: this.form.username,
            password: this.form.password,
            code: this.form.verifyCode,
            checked: this.form.remember,
            userType: 'spaceproxy'
          });

          const { code, data, msg } = res.data || {};

          if (code === 200 && data) {
            localStorage.setItem('Token', data.token);
            localStorage.setItem('Authorization', data.token);
            localStorage.setItem('userName', this.form.username);
            localStorage.setItem('userId', data.uid);
            if (data.userInfo) {
              localStorage.setItem('subNumber', data.userInfo.subNumber);
            }
            this.$message.success("登录成功");
            
            const redirect = this.$route.query.redirect;
            if (redirect) {
              this.$router.push(redirect);
            } else {
              this.$router.push('/dashboard/auth');
            }
          } else {
            this.$message.warning(msg || "登录失败");
            this.getCaptcha();
          }
        } catch (error) {
          console.error('Login error:', error);
          this.$message.error(error.msg || '登录失败，请重试');
          this.getCaptcha();
        } finally {
          this.loading = false;
        }
      });
    }
  }
};
</script>

<style scoped lang="less">
/* Variables */
@bg-dark: #09090b;
@card-bg: #18181b;
@border: #27272a;
@primary: #8b5cf6;
@primary-hover: #a855f7;
@accent: #3b82f6;
@text-main: #f4f4f5;
@text-muted: #a1a1aa;

.login-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: @bg-dark;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: @text-main;
}

/* Background Effects */
.hero-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  overflow: hidden;
  z-index: 0;
  
  .glow-blob {
    position: absolute;
    width: 800px;
    height: 800px;
    border-radius: 50%;
    filter: blur(120px);
    opacity: 0.15;
    background: @primary;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: pulse 8s ease-in-out infinite alternate;
  }
  
  .grid-overlay {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 80%);
  }

  .floating-shape {
    position: absolute;
    z-index: 1;
    opacity: 0.3;
    animation: float 15s infinite linear;
    
    &.shape-1 {
      width: 100px; height: 100px;
      border-radius: 50%;
      background: radial-gradient(circle at top right, @primary, transparent 60%);
      top: 15%; left: 15%;
      animation-delay: 0s;
      filter: blur(20px);
    }
    
    &.shape-2 {
      width: 150px; height: 150px;
      border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
      background: radial-gradient(circle at bottom left, @accent, transparent 60%);
      bottom: 20%; right: 10%;
      animation-delay: -5s;
      filter: blur(30px);
    }
    
    &.shape-3 {
      width: 60px; height: 60px;
      border-radius: 50%;
      border: 2px solid rgba(139, 92, 246, 0.2);
      top: 60%; left: 10%;
      animation-delay: -2s;
    }
    
    &.shape-4 {
      top: 20%; right: 20%;
      color: rgba(59, 130, 246, 0.3);
      width: 40px; height: 40px;
      animation-delay: -7s;
      animation-duration: 20s;
      svg { width: 100%; height: 100%; }
    }
    
    &.shape-5 {
      bottom: 15%; left: 25%;
      color: rgba(139, 92, 246, 0.2);
      width: 30px; height: 30px;
      animation-delay: -11s;
      animation-duration: 25s;
      svg { width: 100%; height: 100%; }
    }
  }
}

@keyframes pulse {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; }
  100% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.25; }
}

@keyframes float {
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-40px) rotate(180deg); }
  100% { transform: translateY(0) rotate(360deg); }
}

.relative-z { position: relative; z-index: 10; }

/* Language Switch */
.lang-switch {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 100;
  
  .lang-btn {
    background: @card-bg;
    border: 1px solid @border;
    color: @text-main;
    padding: 8px 16px;
    border-radius: 100px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      border-color: @primary;
      color: @primary;
    }
  }
}

/* Login Content */
.login-content {
  width: 100%;
  max-width: 440px;
  padding: 20px;
}

.brand-section {
  text-align: center;
  margin-bottom: 40px;
  animation: slideDown 0.8s cubic-bezier(0.22, 1, 0.36, 1);

  .logo-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 16px;
    cursor: pointer;
    transition: transform 0.3s;
    
    &:hover { transform: scale(1.05); }
  }

  .brand-logo-img {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    object-fit: cover;
  }

  .brand-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, @primary, @accent);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    svg { width: 24px; height: 24px; }
  }

  .brand-text {
    font-size: 32px;
    font-weight: 800;
    color: #fff;
    margin: 0;
    letter-spacing: -0.5px;
  }

  .brand-slogan {
    color: @text-muted;
    font-size: 16px;
    margin: 0;
  }
}

.login-card {
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
  position: relative;
  overflow: hidden;
  animation: zoomIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, @primary, transparent);
  }

  .card-header {
    text-align: center;
    margin-bottom: 32px;
    
    h2 { color: #fff; font-size: 24px; font-weight: 700; margin-bottom: 8px; }
    p { color: @text-muted; font-size: 14px; margin: 0; }
  }
}

/* Form Styles */
.input-wrapper {
  background: #000;
  border: 1px solid @border;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 4px 16px;
  transition: all 0.3s ease;
  
  &:hover { border-color: rgba(139, 92, 246, 0.5); }
  
  &:focus-within {
    border-color: @primary;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
    .input-icon { color: @primary; }
  }

  .input-icon {
    color: @text-muted;
    font-size: 18px;
    margin-right: 12px;
    transition: color 0.3s;
  }

  :deep(.ant-input) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: #fff;
    font-size: 15px;
    padding-left: 0;
    height: 40px;
    
    &::placeholder { color: #52525b; }
  }
  
  :deep(.ant-input-password-icon) {
    color: @text-muted;
    &:hover { color: #fff; }
  }
}

.captcha-row {
  display: flex;
  gap: 16px;
  
  .captcha-input { flex: 1; }
  
  .captcha-img-wrapper {
    width: 140px;
    height: 50px;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid @border;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    
    &:hover { border-color: @primary; }
    img { width: 100%; height: 100%; object-fit: contain; }
    .captcha-placeholder { color: @text-muted; font-size: 20px; }
  }
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  
  :deep(.ant-checkbox-wrapper) {
    color: @text-muted;
    font-size: 14px;
    
    &:hover .ant-checkbox-inner { border-color: @primary; }
    
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: @primary;
      border-color: @primary;
    }
    
    .ant-checkbox-inner {
      background-color: transparent;
      border-color: @border;
      border-radius: 4px;
    }
  }
  
  .forgot-link {
    color: @primary;
    font-size: 14px;
    transition: all 0.3s;
    &:hover { color: @primary-hover; text-decoration: underline; }
  }
}

/* Buttons */
.btn-primary {
  background: linear-gradient(135deg, @primary, @accent);
  color: #fff;
  font-weight: 600;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.5);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &.huge {
    height: 52px;
    font-size: 16px;
  }
  
  &.full-width { width: 100%; }
}

.register-link {
  margin-top: 24px;
  text-align: center;
  color: @text-muted;
  font-size: 14px;
  
  a {
    color: @primary;
    font-weight: 600;
    margin-left: 8px;
    transition: all 0.3s;
    &:hover { color: @primary-hover; text-decoration: underline; }
  }
}

.footer-copyright {
  position: absolute;
  bottom: 24px;
  width: 100%;
  text-align: center;
  color: #52525b;
  font-size: 13px;
}

/* Animations */
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

/* Overrides for Ant Form */
:deep(.ant-form-item) {
  margin-bottom: 20px;
}
:deep(.ant-form-item-with-help) {
  margin-bottom: 24px;
}
:deep(.ant-form-explain) {
  color: #ef4444;
  font-size: 13px;
  margin-top: 4px;
}

@media (max-width: 576px) {
  .login-card { padding: 32px 24px; }
  .brand-text { font-size: 28px; }
}
</style>
